import { Select } from '@repo/ui';
import { AdvertisingListUseCaseInterface } from 'domain/usecases';
import { useState } from 'react';
import { TableColumn } from 'react-data-table-component';
import { Input } from 'reactstrap';

interface MakeBannersColumnsParams {
  courseOptions: Array<{ label: string; value: string }>;
  onToggleChange: (
    bannerId: string,
    field: 'showInStudentApp' | 'showInProfessionalApp',
    value: boolean
  ) => void;
  onLinkChange: (bannerId: string, value: string) => void;
  onCoursesChange: (bannerId: string, courseIds: string[]) => void;
  onImageChange: (bannerId: string, file: File) => void;
}

// Componente para o campo de Link com validação
const LinkInput = ({
  rowId,
  linkUrl,
  onLinkChange,
}: {
  rowId: string;
  linkUrl: string;
  onLinkChange: (bannerId: string, value: string) => void;
}) => {
  const [linkError, setLinkError] = useState('');

  return (
    <div className="w-100">
      <Input
        type="url"
        className="form-control-sm"
        defaultValue={linkUrl}
        placeholder="https://exemplo.com"
        onBlur={(e) => {
          const newValue = e.target.value.trim();

          // Validação: não permite vazio
          if (!newValue) {
            setLinkError('O link não pode estar vazio');
            e.target.value = linkUrl; // Restaura o valor anterior
            return;
          }

          // Limpa o erro se válido
          setLinkError('');

          // Se o valor mudou, atualiza
          if (newValue !== linkUrl) {
            onLinkChange(rowId, newValue);
          }
        }}
        onFocus={() => setLinkError('')}
        style={{
          fontSize: '0.875rem',
          borderColor: linkError ? '#dc3545' : undefined,
        }}
      />
      {linkError && (
        <small
          style={{
            color: '#dc3545',
            fontSize: '0.75rem',
            display: 'block',
            marginTop: '2px',
          }}
        >
          {linkError}
        </small>
      )}
    </div>
  );
};

// Componente para seleção de cursos com regra: mínimo de 1 curso
const CoursesCell = ({
  row,
  courseOptions,
  onCoursesChange,
}: {
  row: AdvertisingListUseCaseInterface.Result;
  courseOptions: Array<{ label: string; value: string }>;
  onCoursesChange: (bannerId: string, courseIds: string[]) => void;
}) => {
  const [error, setError] = useState('');

  const selectedCourses = (row?.course || [])
    .map((course) => courseOptions.find((option) => option.value === course.id))
    .filter(
      (option): option is { label: string; value: string } => option !== undefined
    );

  const hasNoneSelected = selectedCourses.length === 0;

  return (
    <div className="w-100">
      <Select
        isMulti
        value={selectedCourses}
        options={courseOptions}
        placeholder="Selecione..."
        menuPortalTarget={document.body}
        // Regra: não permitir ficar vazio
        onChange={(value: any) => {
          const selected = Array.isArray(value) ? value : value ? [value] : [];
          if (selected.length === 0) {
            setError('Selecione ao menos 1 curso');
            return; // Não propaga a mudança se ficar vazio
          }
          setError('');
          const courseIds = selected.map((item) => item.value);
          onCoursesChange(row.id, courseIds);
        }}
        className="w-100"
      />
      {(error || hasNoneSelected) && (
        <small
          className="text-danger d-block mt-1"
          style={{ fontSize: '0.75rem' }}
        >
          {error || 'Selecione ao menos 1 curso'}
        </small>
      )}
    </div>
  );
};

export const makeAdvertisingColumns = ({
  courseOptions,
  onLinkChange,
  onImageChange,
  onToggleChange,
  onCoursesChange,
}: MakeBannersColumnsParams): TableColumn<AdvertisingListUseCaseInterface.Result>[] => {
  return [
    {
      name: 'Imagem',
      width: '150px',
      cell: (row) => {
        const inputId = `image-upload-${row.id}`;

        return (
          <div className="d-flex my-2">
            <label
              htmlFor={inputId}
              style={{
                margin: 0,
                padding: 0,
                position: 'relative',
                display: 'inline-block',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                const overlay = e.currentTarget.querySelector(
                  '.image-overlay'
                ) as HTMLElement;
                if (overlay) overlay.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const overlay = e.currentTarget.querySelector(
                  '.image-overlay'
                ) as HTMLElement;
                if (overlay) overlay.style.opacity = '0';
              }}
            >
              <img
                src={row.photo}
                alt="Banner"
                style={{
                  width: '120px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  display: 'block',
                }}
              />
              <div
                className="image-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none',
                }}
              >
                <i
                  className="ri-camera-fill"
                  style={{
                    fontSize: '24px',
                    color: 'white',
                  }}
                ></i>
              </div>
            </label>
            <input
              id={inputId}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onImageChange(row.id, file);
                  e.target.value = '';
                }
              }}
            />
          </div>
        );
      },
    },
    {
      name: 'Link',
      minWidth: '250px',
      cell: (row) => (
        <LinkInput
          rowId={row.id}
          linkUrl={row.linkUrl}
          onLinkChange={onLinkChange}
        />
      ),
    },
    {
      name: 'Cursos',
      minWidth: '250px',
      cell: (row) => (
        <CoursesCell
          row={row}
          courseOptions={courseOptions}
          onCoursesChange={onCoursesChange}
        />
      ),
    },
    {
      name: 'App Estudante',
      width: '140px',
      center: true,
      cell: (row) => (
        <div className="form-check form-switch form-switch-success">
          <Input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`student-${row.id}`}
            checked={row.showInStudentApp}
            onChange={(e) =>
              onToggleChange(row.id, 'showInStudentApp', e.target.checked)
            }
          />
        </div>
      ),
    },
    {
      name: 'App Profissional',
      width: '160px',
      center: true,
      cell: (row) => (
        <div className="form-check form-switch form-switch-success">
          <Input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`professional-${row.id}`}
            checked={row.showInProfessionalApp}
            onChange={(e) =>
              onToggleChange(row.id, 'showInProfessionalApp', e.target.checked)
            }
          />
        </div>
      ),
    },
  ];
};
